import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Database,
  Building2,
  CalendarDays,
  ChevronDown,
  Activity,
  FileSignature,
  Zap,
  Mail,
  BarChart3,
  Receipt,
  Scale,
  Shield,
  Printer,
  Euro,
  Server,
  Plug,
  Contact,
  Cpu,
  FileInput,
  FileText,
  Wrench,
  LayoutDashboard,
  Settings2,
  Ticket,
  LogOut,
  Menu,
  MessageCircle,
  MessageSquare,
  Monitor,
  MonitorPlay,
  MonitorSmartphone,
  Moon,
  ScrollText,
  ShieldCheck,
  Sun,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { NativeBridge } from '@/components/native-bridge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth';
import { logoUrl } from '@/lib/branding';
import { useTheme } from '@/lib/theme';
import { cn, initials } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: string;
  platformOnly?: boolean;
}

/** Hauptbereich (immer sichtbar) und einklappbare Gruppen der Administration. */
const NAV: { title?: string; collapsible?: boolean; items: NavItem[] }[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard.view' },
      { to: '/tickets', label: 'Tickets', icon: Ticket, permission: 'tickets.read' },
      { to: '/repairs', label: 'Reparaturen', icon: Wrench, permission: 'repairs.read' },
      { to: '/customers', label: 'Kunden', icon: Contact, permission: 'customers.read' },
      { to: '/devices', label: 'Geräte', icon: Cpu, permission: 'devices.read' },
      { to: '/contracts', label: 'Verträge', icon: FileSignature, permission: 'contracts.read' },
      { to: '/calendar', label: 'Kalender', icon: CalendarDays, permission: 'calendar.read' },
      { to: '/monitoring', label: 'Monitoring', icon: Activity, permission: 'monitoring.read' },
      { to: '/forms', label: 'Formulare', icon: FileInput, permission: 'forms.read' },
      { to: '/billing', label: 'Abrechnung', icon: Receipt, permission: 'billing.read' },
      { to: '/reports', label: 'Berichte', icon: BarChart3, permission: 'reports.read' },
    ],
  },
  {
    title: 'Benutzer & Rechte',
    collapsible: true,
    items: [
      { to: '/admin/users', label: 'Benutzer', icon: Users, permission: 'admin.users.read' },
      { to: '/admin/roles', label: 'Rollen & Rechte', icon: ShieldCheck, permission: 'admin.users.read' },
      { to: '/admin/rates', label: 'Stundensätze', icon: Euro, permission: 'admin.users.manage' },
    ],
  },
  {
    title: 'Service',
    collapsible: true,
    items: [
      { to: '/admin/ticket-config', label: 'Ticket-Konfiguration', icon: Settings2, permission: 'tickets.configure' },
      { to: '/admin/automations', label: 'Automatisierungen', icon: Zap, permission: 'automations.manage' },
      { to: '/admin/monitors', label: 'Statusmonitore', icon: MonitorPlay, permission: 'admin.tenant.manage' },
    ],
  },
  {
    title: 'Integrationen',
    collapsible: true,
    items: [
      { to: '/admin/mail', label: 'E-Mail-Versand', icon: Mail, permission: 'admin.tenant.manage' },
      { to: '/admin/mail-templates', label: 'E-Mail-Vorlagen', icon: FileText, permission: 'admin.tenant.manage' },
      { to: '/admin/integrations/m365', label: 'Microsoft 365', icon: Plug, permission: 'integrations.manage' },
      { to: '/admin/integrations/teams', label: 'Microsoft Teams', icon: MessageSquare, permission: 'integrations.manage' },
      { to: '/admin/integrations/whatsapp', label: 'WhatsApp', icon: MessageCircle, permission: 'integrations.manage' },
      { to: '/admin/integrations/monitoring', label: 'Monitoring & RMM', icon: Server, permission: 'integrations.manage' },
      { to: '/admin/integrations/powerbird', label: 'Powerbird', icon: Database, permission: 'integrations.manage' },
      { to: '/admin/printers', label: 'Drucker', icon: Printer, permission: 'admin.tenant.manage' },
    ],
  },
  {
    title: 'Unternehmen',
    collapsible: true,
    items: [
      { to: '/admin/tenant', label: 'Unternehmen', icon: Building2, permission: 'admin.tenant.manage' },
      { to: '/admin/privacy', label: 'Datenschutz', icon: Shield, permission: 'admin.tenant.manage' },
      { to: '/admin/legal', label: 'Rechtstexte', icon: Scale, permission: 'admin.tenant.manage' },
      { to: '/admin/audit', label: 'Audit-Log', icon: ScrollText, permission: 'admin.audit.read' },
    ],
  },
  {
    title: 'Plattform',
    collapsible: true,
    items: [
      { to: '/platform/tenants', label: 'Mandanten', icon: Building2, permission: 'platform.tenants.manage', platformOnly: true },
      { to: '/platform/apps', label: 'Apps', icon: MonitorSmartphone, permission: 'platform.tenants.manage', platformOnly: true },
    ],
  },
];

const NAV_STATE_KEY = 'sp-nav-open';
function readNavState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(NAV_STATE_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

function Brand() {
  const { me } = useAuth();
  if (!me) return null;
  return (
    <div className="flex h-14 items-center gap-2.5 px-4">
      {me.tenant.hasLogo ? (
        <img src={logoUrl(me.tenant.brandingVersion)} alt="" className="h-8 max-w-[140px] object-contain" />
      ) : (
        <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">{me.tenant.name.charAt(0)}</div>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold leading-tight">{me.tenant.name}</p>
        <p className="text-xs text-muted-foreground">Serviceportal</p>
      </div>
    </div>
  );
}

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState<Record<string, boolean>>(readNavState);
  const toggle = (title: string, value: boolean) =>
    setOpen((o) => {
      const next = { ...o, [title]: value };
      try {
        localStorage.setItem(NAV_STATE_KEY, JSON.stringify(next));
      } catch {
        /* nur Komfort */
      }
      return next;
    });
  const link = (it: NavItem) => (
    <NavLink
      key={it.to}
      to={it.to}
      end={it.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
          isActive && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary',
        )
      }
    >
      <it.icon className="size-4" />
      {it.label}
    </NavLink>
  );
  const visible = NAV.map((section) => ({
    ...section,
    items: section.items.filter((it) => can(it.permission) && (!it.platformOnly || me?.tenant.isPlatform)),
  })).filter((s) => s.items.length > 0);
  const hasAdmin = visible.some((s) => s.collapsible && s.title !== 'Plattform');
  return (
    <nav className="flex flex-col gap-1 px-3 py-4">
      {visible.map((section, i) => {
        if (!section.collapsible) {
          return (
            <div key={i} className="mb-3 flex flex-col gap-0.5">
              {section.items.map(link)}
            </div>
          );
        }
        const title = section.title!;
        const containsActive = section.items.some((it) => pathname === it.to || pathname.startsWith(`${it.to}/`));
        const isOpen = open[title] ?? containsActive;
        const firstAdmin = hasAdmin && visible.findIndex((s) => s.collapsible) === i;
        return (
          <div key={title} className="flex flex-col gap-0.5">
            {firstAdmin && <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Administration</p>}
            <button
              type="button"
              onClick={() => toggle(title, !isOpen)}
              aria-expanded={isOpen}
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground',
                containsActive && !isOpen && 'text-primary',
              )}
            >
              {title}
              <ChevronDown className={cn('size-4 transition-transform', !isOpen && '-rotate-90')} />
            </button>
            {isOpen && <div className="ml-2 flex flex-col gap-0.5 border-l pl-1.5">{section.items.map(link)}</div>}
          </div>
        );
      })}
    </nav>
  );
}

function ThemeMenuItems() {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: 'light', label: 'Hell', icon: Sun },
    { value: 'dark', label: 'Dunkel', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;
  return (
    <>
      {options.map((o) => (
        <DropdownMenuItem key={o.value} onSelect={() => setTheme(o.value)} className={cn(theme === o.value && 'font-semibold')}>
          <o.icon />
          {o.label}
        </DropdownMenuItem>
      ))}
    </>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full p-0.5 pr-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{initials(me.user.firstName, me.user.lastName)}</span>
          <span className="hidden text-sm font-medium sm:block">{me.user.firstName}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          <p className="font-medium">
            {me.user.firstName} {me.user.lastName}
          </p>
          <p className="truncate text-xs text-muted-foreground">{me.user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/profile')}>
          <UserRound />
          Mein Profil
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <ThemeMenuItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await logout();
            navigate('/login');
          }}
        >
          <LogOut />
          Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Navigation öffnen">
          <Menu />
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
        <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] overflow-y-auto border-r bg-sidebar shadow-xl focus:outline-none lg:hidden">
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          <div className="flex items-center justify-between border-b pr-2">
            <Brand />
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Navigation schließen">
                <X />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <Navigation onNavigate={() => setOpen(false)} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r bg-sidebar lg:flex">
        <div className="border-b">
          <Brand />
        </div>
        <div className="flex-1 overflow-y-auto">
          <Navigation />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
          <MobileNav />
          <div className="flex-1" />
          <UserMenu />
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8 3xl:max-w-[110rem] 4xl:max-w-none 4xl:px-12">{children ?? <Outlet />}</main>
        <NativeBridge />
      </div>
    </div>
  );
}
